import { FLAG_DROP, encodeMove, moveTo, parseSquare, squareName } from '../board.ts';
import { DUCK } from '../pieces.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, win } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 5 (draft). Duck Chess with a goose. Every turn has two parts: move a piece, then move
// the goose (a neutral piece) to any other empty square. Nothing can land on or pass through
// the goose. There's no check: you may leave your king attacked, and capturing the king wins.
// A player with no legal move wins (as in Duck Chess).
//
// ext: [phase (0 = piece move, 1 = goose move), goose square or -1, saved en passant square]

const PHASE = 0;
const GOOSE = 1;
const EP = 2;

export const GOOSE_FEN = `${STANDARD_FEN} p - -`;

export const goose: Variant = {
  id: 'goose',
  info: {
    name: 'Goose Chess',
    but: "there's a goose",
    tagline: 'After every move you put the goose somewhere annoying. Nothing gets past the goose.',
    summary: 'After every move you **move the goose** to any empty square. Nothing can land on or pass through it. No check: take the king to win.',
    rules: [
      'Every turn: move a piece, then move the goose to any other empty square.',
      'Nothing can land on or pass through the goose. It can’t be captured.',
      'There’s no check. You may leave your king attacked, and capturing the king wins.',
      'If you can’t move any piece, you win (that’s the goose’s fault).',
      'Castling, en passant and promotion work as usual; repetition and the 50-move rule draw.',
    ],
    accent: '#4cc9f0',
    emoji: '🪿',
    bots: ['Gosling', 'Park Goose', 'Angry Goose', 'Canada Goose', 'Untitled Goose'],
  },
  startFen: GOOSE_FEN,
  extraPieces: [DUCK],
  checks: false,
  extSize: 3,
  fenSkip: [DUCK],

  parseExtra(fields, pos) {
    const ext = pos.ext!;
    const [phase = 'p', square = '-', ep = '-'] = fields;
    if (phase !== 'p' && phase !== 'g') throw new FenError('Bad goose phase');
    const g = square === '-' ? -1 : parseSquare(square);
    if (square !== '-' && (g < 0 || pos.board[g] !== 0)) throw new FenError('Goose must be on an empty square');
    ext[PHASE] = phase === 'g' ? 1 : 0;
    ext[GOOSE] = g;
    ext[EP] = ep === '-' ? -1 : parseSquare(ep);
    if (g >= 0) pos.board[g] = DUCK;
  },

  formatExtra(pos) {
    const ext = pos.ext!;
    return [ext[PHASE] ? 'g' : 'p', ext[GOOSE] >= 0 ? squareName(ext[GOOSE]) : '-', ext[EP] >= 0 ? squareName(ext[EP]) : '-'];
  },

  replaceMoves(pos, out, capturesOnly) {
    const ext = pos.ext!;
    if (ext[PHASE] !== 1) return false;
    if (capturesOnly) return true;
    const g = ext[GOOSE];
    for (let sq = 0; sq < 64; sq++) {
      if (pos.board[sq] !== 0) continue;
      out.push(g < 0 ? encodeMove(DUCK, sq, 0, FLAG_DROP) : encodeMove(g, sq));
    }
    return true;
  },

  afterMove(ctx: MoveContext) {
    const { next, prev } = ctx;
    const ext = next.ext!;
    if (prev.ext![PHASE] === 1) {
      // The goose moved: the turn passes, en passant (from the piece move) comes back, and
      // the goose's hop doesn't count towards the 50-move rule.
      ext[PHASE] = 0;
      ext[GOOSE] = moveTo(ctx.move);
      next.ep = ext[EP];
      ext[EP] = -1;
      next.halfmove = prev.halfmove;
      return;
    }
    if (next.royals[ctx.them] >= 0) {
      ext[PHASE] = 1;
      ext[EP] = next.ep;
    }
  },

  nextTurn(ctx) {
    return ctx.next.ext![PHASE] === 1 ? ctx.us : ctx.them;
  },

  noMoves(pos) {
    return win(pos.turn, 'no-moves-win');
  },

  reasonText: {
    'king-captured': 'The king was captured',
    'no-moves-win': 'No moves left, and in Goose Chess that’s a win',
  },
};

/** Is the side to move placing the goose right now? */
export function goosePhase(pos: { ext: Int32Array | null }): boolean {
  return pos.ext?.[PHASE] === 1;
}

export function gooseSquare(pos: { ext: Int32Array | null }): number {
  return pos.ext?.[GOOSE] ?? -1;
}

